import {
  Button,
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
  cn,
} from '@design-system';
import {
  AddPartnerDialog,
  EditPartnerDialog,
  InvitePartnerDialog,
  LinkExistingPartnerDialog,
} from '@/features/add-partner';
import { EditCareAssumptionsModal } from '@/components';

import { Users, Settings2, ChevronDown, Link, UserPlus, Mail } from 'lucide-react';

import { useState } from 'react';
import { useSelector } from 'react-redux';

import { selectSession, selectFeatureFlags } from '@/model/selectors';
import { isNullOrUndefined, SessionType } from '@shared';
import { NylPilotClientCreationRestricted } from '@/shared/components/nyl-pilot-client-creation-restricted';

type CustomizePlanButtonProps = {
  client: Client;
  className?: string;
  showLinkPartnerBtn?: boolean;
  size?: 'sm' | 'default' | 'lg';
};

type PartnerActionButtonProps = {
  show: boolean;
  isAdvisor: boolean;
  hasPartner: boolean;
  onEditPartner: () => void;
  onLinkExisting: () => void;
  onAddNew: () => void;
  onInvitePartner: () => void;
  size?: 'sm' | 'default' | 'lg';
};

function PartnerActionButton({
  show,
  isAdvisor,
  hasPartner,
  onEditPartner,
  onLinkExisting,
  onAddNew,
  onInvitePartner,
  size = 'sm',
}: PartnerActionButtonProps) {
  if (!show) return null;

  if (hasPartner) {
    return (
      <Button
        size={size}
        variant="outline"
        onClick={onEditPartner}
      >
        <Users />
        Edit partner
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="group w-full focus-visible:outline-none">
        <Button
          variant="outline"
          size={size}
        >
          <Users />
          Add partner
          <ChevronDown className="shrink-0 transition-transform group-data-[state=open]:rotate-180" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-full">
        {isAdvisor ? (
          <>
            <DropdownMenuItem onClick={onLinkExisting}>
              <Link className="size-4" />
              Link Existing Client as Partner
            </DropdownMenuItem>
            <NylPilotClientCreationRestricted capability="add-partner">
              <DropdownMenuItem onClick={onAddNew}>
                <UserPlus className="size-4" />
                Add new partner
              </DropdownMenuItem>
            </NylPilotClientCreationRestricted>
          </>
        ) : (
          <>
            <DropdownMenuItem onClick={onAddNew}>
              <UserPlus className="size-4" />
              Add Partner
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onInvitePartner}>
              <Mail className="size-4" />
              Invite Partner
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function CustomizePlanButtonGroup({
  className,
  client,
  showLinkPartnerBtn = true,
  size = 'sm',
}: CustomizePlanButtonProps) {
  const { mutableClientPartner } = client;
  const { sessionType } = useSelector(selectSession);
  const featureFlags = useSelector(selectFeatureFlags);
  const isD2C = sessionType === SessionType.client && !!featureFlags.d2cMode;

  const [openAddPartnerModal, setOpenAddPartnerModal] = useState(false);
  const [openStressTestModal, setOpenStressTestModal] = useState(false);
  const [openEditPartnerModal, setOpenEditPartnerModal] = useState(false);
  const [openLinkExistingModal, setOpenLinkExistingModal] = useState(false);
  const [openInvitePartnerModal, setOpenInvitePartnerModal] = useState(false);

  const isAdvisor = sessionType === SessionType.advisor || sessionType === SessionType.wizard;
  const hasPartner = !isNullOrUndefined(mutableClientPartner?.clientId);
  const showLinkPartnerBtnOverride = showLinkPartnerBtn && !isD2C && (isAdvisor || !hasPartner);

  return (
    <>
      <div className={cn('flex items-center gap-2', className)}>
        <Button
          size={size}
          variant="outline"
          onClick={() => setOpenStressTestModal(true)}
          data-test-id="edit-care-assumptions-button"
        >
          <Settings2 />
          Edit care assumptions
        </Button>
        <PartnerActionButton
          size={size}
          isAdvisor={isAdvisor}
          hasPartner={hasPartner}
          show={showLinkPartnerBtnOverride}
          onAddNew={() => setOpenAddPartnerModal(true)}
          onEditPartner={() => setOpenEditPartnerModal(true)}
          onLinkExisting={() => setOpenLinkExistingModal(true)}
          onInvitePartner={() => setOpenInvitePartnerModal(true)}
        />
      </div>

      <EditCareAssumptionsModal
        open={openStressTestModal}
        setOpen={setOpenStressTestModal}
      />

      {isAdvisor && (
        <LinkExistingPartnerDialog
          open={openLinkExistingModal}
          client={client}
          onClose={() => setOpenLinkExistingModal(false)}
        />
      )}

      <AddPartnerDialog
        open={openAddPartnerModal}
        onOpenChange={isOpen => {
          if (!isOpen) setOpenAddPartnerModal(false);
        }}
        clientId={client.clientId}
      />

      <InvitePartnerDialog
        open={openInvitePartnerModal}
        onOpenChange={setOpenInvitePartnerModal}
      />

      {mutableClientPartner && (
        <EditPartnerDialog
          open={openEditPartnerModal}
          onOpenChange={isOpen => {
            if (!isOpen) setOpenEditPartnerModal(false);
          }}
          client={client}
          partner={mutableClientPartner}
        />
      )}
    </>
  );
}
